import { FilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import { BooleanExpr, ClickQuery } from '@aidkitorg/types/lib/survey';
import { useCallback, useEffect, useRef } from 'react';
import { usePost } from '../../../API';
import {
  collectApplicantGroupIdsFromConfig,
  expandApplicantGroupReferences,
  expandBooleanExprApplicantGroupReferences,
  FetchApplicantGroupById,
  removeApplicantGroupConditionsByIds
} from '../utils/applicantGroupFilterUtils';

/**
 * Shared utilities for managing applicant group references inside filters:
 *  - {@link useApplicantGroupExpansion}: inline a referenced group's filter at query time.
 *  - {@link useDeletedApplicantGroupCleanup}: strip conditions whose group no longer exists.
 *
 * Both talk to the same `/distro/applicant_group/get` endpoint with `suppressErrorDisplay`,
 * because a referenced applicant group may have been deleted since the filter was saved.
 * That's an expected, recoverable state, so we must not surface the backend's
 * "Could not find Applicant Group with id ..." error as a toast.
 */

/**
 * Custom hook that provides utilities for expanding applicant group references in filters.
 *
 * @returns Object containing expansion functions for FilterConfig and BooleanExpr format
 */
export function useApplicantGroupExpansion() {
  // See file-level note for why errors are suppressed: a deleted group is recoverable
  // (the expansion logic drops the unresolved reference), not a user-facing error.
  const fetchApplicantGroupByIdPost = usePost('/distro/applicant_group/get', { suppressErrorDisplay: true });

  const createFetchGroupById = useCallback((): FetchApplicantGroupById => {
    return async (id: string) => {
      const response = await fetchApplicantGroupByIdPost({ id });
      const result = response?.result;

      // Distro returns ApplicantGroup with 'filter: BooleanExpr'
      // We need to transform it to the expected format: { filters: FilterConfig }
      if (result && result.filter) {
        return {
          filters: {
            enabled: true,
            // The filter is a BooleanExpr, which will be handled by the expansion logic
            config: result.filter as any
          }
        };
      }
      throw new Error(`Failed to fetch applicant group ${id}`);
    };
  }, [fetchApplicantGroupByIdPost]);

  /**
   * Expands applicant group references in a FilterConfig.
   * Used for viewFilters created by the FilterPanel UI component.
   *
   * @param filterConfig - The FilterConfig to expand
   * @returns Promise resolving to the expanded FilterConfig, or original on error
   */
  const expandFilterConfig = useCallback(
    async (filterConfig: FilterConfig): Promise<FilterConfig> => {
      if (!filterConfig?.enabled || !filterConfig?.config || !filterConfig?.config?.conditions?.length) {
        return filterConfig;
      }

      try {
        const fetchGroupById = createFetchGroupById();
        return await expandApplicantGroupReferences(filterConfig, fetchGroupById);
      } catch (err) {
        console.error('[useApplicantGroupExpansion] Failed to expand FilterConfig:', err);
        return filterConfig; // Fall back to original on error
      }
    },
    [createFetchGroupById]
  );

  /**
   * Expands applicant group references in a BooleanExpr or ClickQuery, dropping references to
   * deleted applicant groups (rather than substituting a permissive match-all clause). Returns
   * `undefined` when the expression resolves away entirely (e.g. its only condition referenced a
   * deleted group), so a stale reference can never silently broaden the filter.
   *
   * @param booleanExpr - The BooleanExpr or ClickQuery to expand
   * @returns Promise resolving to the expanded filter, or undefined if nothing remains/on error
   */
  const expandBooleanExpr = useCallback(
    async (
      booleanExpr: BooleanExpr | ClickQuery | undefined
    ): Promise<BooleanExpr | undefined> => {
      if (!booleanExpr) {
        return undefined;
      }

      try {
        const fetchGroupById = createFetchGroupById();
        return await expandBooleanExprApplicantGroupReferences(booleanExpr, fetchGroupById);
      } catch (err) {
        console.error('[useApplicantGroupExpansion] Failed to expand BooleanExpr:', err);
        // Fail closed: better to drop the filter than to risk applying a stale/permissive one.
        return undefined;
      }
    },
    [createFetchGroupById]
  );

  /**
   * Expands applicant group references in a config object's filter field.
   * This is a convenience wrapper around expandBooleanExpr for config objects.
   *
   * @param config - The config object containing an optional filter field
   * @returns Promise resolving to the config with expanded filter, or original config on error
   */
  const expandConfigFilter = useCallback(
    async <T extends { filter?: BooleanExpr | ClickQuery }>(
      config: T
    ): Promise<T> => {
      if (!config.filter) {
        return config;
      }

      try {
        const fetchGroupById = createFetchGroupById();
        const expandedFilter = await expandBooleanExprApplicantGroupReferences(
          config.filter,
          fetchGroupById
        );

        // When the filter resolves away entirely (only referenced deleted groups), drop it
        // rather than keeping the original unexpanded synthetic reference.
        return { ...config, filter: expandedFilter };
      } catch (err) {
        console.error('[useApplicantGroupExpansion] Failed to expand config filter:', err);
        return config; // Fall back to original on error
      }
    },
    [createFetchGroupById]
  );

  return {
    expandFilterConfig,
    expandBooleanExpr,
    expandConfigFilter
  };
}

/**
 * Session-scoped cache of applicant-group existence keyed by group id. Shared across hook
 * instances so we only look up each group once per page session (group ids are immutable
 * UUIDs, so a "missing" result can't flip back to "exists" within a session).
 */
const groupExistenceCache = new Map<string, boolean>();

/**
 * Watches a view FilterConfig for applicant_group conditions that reference applicant groups
 * which have since been deleted, and transparently removes those conditions.
 *
 * Rather than showing the backend's "Could not find Applicant Group with id ..." error
 * (suppressed in {@link useApplicantGroupExpansion}) and leaving a phantom condition in
 * place, this prunes the stale condition and hands the cleaned filter to `onPrune` so the
 * caller can persist it (and re-run the query).
 *
 * Note: we deliberately do NOT prune through the FilterPanel's `applyConfig` ref — the panel
 * is only mounted when the table has data, so a filter that returns no rows (the common case
 * for a deleted-group reference) would leave the ref null and the condition un-pruned. Going
 * through the caller's persisted setter works regardless of whether the panel is mounted.
 *
 * @param filterConfig - The currently applied/editable view filter.
 * @param onPrune - Called with the cleaned filter when one or more deleted-group conditions
 *   are removed. The caller is responsible for persisting it and re-applying the query.
 */
export function useDeletedApplicantGroupCleanup(
  filterConfig: FilterConfig | undefined,
  onPrune: (prunedConfig: FilterConfig) => void
) {
  const fetchApplicantGroupByIdPost = usePost('/distro/applicant_group/get', { suppressErrorDisplay: true });

  // Keep the latest values without forcing the validation effect to re-run on every render.
  const filterConfigRef = useRef(filterConfig);
  filterConfigRef.current = filterConfig;
  const fetchRef = useRef(fetchApplicantGroupByIdPost);
  fetchRef.current = fetchApplicantGroupByIdPost;
  const onPruneRef = useRef(onPrune);
  onPruneRef.current = onPrune;

  const referencedIds = collectApplicantGroupIdsFromConfig(filterConfig);
  // Stable key: only re-validate when the set of referenced group ids actually changes.
  const referencedIdsKey = referencedIds.slice().sort().join('|');

  useEffect(() => {
    if (referencedIds.length === 0) return;

    let cancelled = false;
    (async () => {
      const missing = new Set<string>();
      for (const id of referencedIds) {
        let exists = groupExistenceCache.get(id);
        if (exists === undefined) {
          try {
            const response = await fetchRef.current({ id });
            // A missing group returns no `result` (the backend error is suppressed).
            exists = !!(response && (response as { result?: unknown }).result);
          } catch {
            // Transient/network failure — assume the group exists so we never prune a
            // condition just because a lookup happened to fail.
            exists = true;
          }
          groupExistenceCache.set(id, exists);
        }
        if (!exists) missing.add(id);
      }

      if (cancelled || missing.size === 0) return;

      const current = filterConfigRef.current;
      if (!current) return;

      const { config: prunedConfig, removed } = removeApplicantGroupConditionsByIds(current, missing);
      if (removed) {
        onPruneRef.current(prunedConfig);
      }
    })();

    return () => {
      cancelled = true;
    };
    // referencedIdsKey captures changes to the set of referenced ids.
  }, [referencedIdsKey]);
}
